import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  FileSignature,
  Search,
  Filter,
  Building2,
  Calendar,
  ChevronRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import api, { adminAgreementsAPI } from '../../services/api';

export default function AdminAgreementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const lastRefreshTimestamp = useRef(null);
  const [agreements, setAgreements] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'active');
  const [companyFilter, setCompanyFilter] = useState(searchParams.get('company') || '');
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: 25,
    total: 0,
  });

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get('/admin/companies?limit=500');
        setCompanies(response.data.companies || []);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  const fetchAgreements = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: pagination.limit,
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter) params.companyId = companyFilter;

      const response = await adminAgreementsAPI.list(params);
      setAgreements(response.data.agreements || []);
      setPagination((prev) => ({
        ...prev,
        page,
        total: response.data.pagination?.total || response.data.agreements?.length || 0,
      }));

      // Update URL params
      const newParams = new URLSearchParams();
      if (page > 1) newParams.set('page', page);
      if (search) newParams.set('search', search);
      if (statusFilter !== 'active') newParams.set('status', statusFilter);
      if (companyFilter) newParams.set('company', companyFilter);
      setSearchParams(newParams, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements(pagination.page);
  }, [statusFilter, companyFilter]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      fetchAgreements(pagination.page);
    }
  }, [location.state?.refresh]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAgreements(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (agreement) => {
    if (agreement.cancelledFlag) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">
          Cancelled
        </span>
      );
    }
    if (agreement.expiredFlag) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
          Expired
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
        Active
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Agreements</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          View and manage customer service agreements
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or company..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-neutral-400" />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[200px] bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company.cw_company_id} value={company.cw_company_id}>
                {company.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="active">Active Only</option>
            <option value="cancelled">Cancelled Only</option>
            <option value="all">All Agreements</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Agreements Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : agreements.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <FileSignature className="w-12 h-12 mx-auto mb-4 text-neutral-400 dark:text-neutral-500" />
          <p className="text-neutral-500 dark:text-neutral-400">No agreements found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Agreement
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {agreements.map((agreement) => (
                  <tr
                    key={agreement.id}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                    onClick={() => {
                      sessionStorage.setItem('adminAgreementsReturnUrl', window.location.pathname + window.location.search);
                      window.location.href = `/admin/agreements/${agreement.id}`;
                    }}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/agreements/${agreement.id}`}
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {agreement.name}
                      </Link>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        #{agreement.id}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-neutral-400" />
                        <span className="text-neutral-900 dark:text-white truncate max-w-[200px]">
                          {agreement.company?.name || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {agreement.type?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-neutral-400" />
                        {formatDate(agreement.startDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {formatDate(agreement.endDate)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(agreement)}
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination - simple version */}
      {agreements.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Showing {agreements.length} agreements
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchAgreements(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
            >
              Previous
            </button>
            <span className="text-sm text-neutral-600 dark:text-neutral-400 px-2">
              Page {pagination.page}
            </span>
            <button
              onClick={() => fetchAgreements(pagination.page + 1)}
              disabled={agreements.length < pagination.limit}
              className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
